import { useState, useEffect, useCallback } from 'react';
import { X, Download, Smartphone, Bell, Share, Check, ChevronRight } from 'lucide-react';
import { Button } from './ui/button';

// Detect device type and browser
const getDeviceInfo = () => {
  const ua = navigator.userAgent;
  const isIOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
  const isAndroid = /Android/.test(ua);
  const isSafari = /^((?!chrome|android).)*safari/i.test(ua);
  const isChrome = /Chrome/.test(ua) && !/Edge/.test(ua);
  const isFirefox = /Firefox/.test(ua);
  const isStandalone = window.matchMedia('(display-mode: standalone)').matches || 
                       window.navigator.standalone || 
                       document.referrer.includes('android-app://');
  
  return { isIOS, isAndroid, isSafari, isChrome, isFirefox, isStandalone };
};

// PWA Install Prompt Component
export const PWAInstallPrompt = () => {
  const [showPrompt, setShowPrompt] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [deviceInfo, setDeviceInfo] = useState({});
  const [showIOSInstructions, setShowIOSInstructions] = useState(false);
  const [installStep, setInstallStep] = useState(0);

  useEffect(() => {
    const info = getDeviceInfo();
    setDeviceInfo(info);

    // Already installed - don't show
    if (info.isStandalone) return;

    // Check if user dismissed recently
    const dismissed = localStorage.getItem('pwa-install-dismissed');
    if (dismissed) {
      const dismissedTime = parseInt(dismissed, 10);
      const daysSinceDismissed = (Date.now() - dismissedTime) / (1000 * 60 * 60 * 24);
      if (daysSinceDismissed < 7) return; // Wait 7 days before showing again
    }

    // Listen for beforeinstallprompt (Android/Chrome)
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      // Show after 30 seconds of browsing
      setTimeout(() => setShowPrompt(true), 30000);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // For iOS Safari - show after delay
    if (info.isIOS && info.isSafari) {
      setTimeout(() => setShowPrompt(true), 45000);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstall = useCallback(async () => {
    if (deferredPrompt) {
      // Chrome/Android install
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      
      if (outcome === 'accepted') {
        setShowPrompt(false);
        localStorage.setItem('pwa-installed', 'true');
      }
      setDeferredPrompt(null);
    } else if (deviceInfo.isIOS) {
      // Show iOS instructions
      setShowIOSInstructions(true);
    }
  }, [deferredPrompt, deviceInfo.isIOS]);

  const handleDismiss = useCallback(() => {
    setShowPrompt(false);
    setShowIOSInstructions(false);
    localStorage.setItem('pwa-install-dismissed', Date.now().toString());
  }, []);

  if (!showPrompt || deviceInfo.isStandalone) return null;

  // iOS Instructions Modal
  if (showIOSInstructions) {
    const iosSteps = [
      { icon: Share, text: 'Tap the Share button', subtext: 'At the bottom of Safari' },
      { icon: Download, text: 'Scroll down and tap "Add to Home Screen"', subtext: 'In the share menu' },
      { icon: Check, text: 'Tap "Add" in the top right', subtext: 'Afriqoma will appear on your home screen' }
    ];

    return (
      <div className="fixed inset-0 z-[100] bg-black/50 backdrop-blur-sm flex items-end sm:items-center justify-center p-4">
        <div className="bg-white rounded-t-3xl sm:rounded-2xl w-full max-w-md animate-slide-up overflow-hidden">
          <div className="bg-gradient-to-br from-primary to-orange-500 p-6 text-white">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold">Install Afriqoma</h3>
              <button 
                onClick={handleDismiss}
                className="p-2 hover:bg-white/20 rounded-full transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <p className="text-white/90 text-sm">Follow these steps to add Afriqoma to your home screen</p>
          </div>
          
          <div className="p-6 space-y-4">
            {iosSteps.map((step, index) => {
              const Icon = step.icon;
              const isActive = index === installStep;
              const isComplete = index < installStep;
              
              return (
                <div 
                  key={step.text}
                  className={`flex items-start gap-4 p-4 rounded-xl transition-all ${
                    isActive ? 'bg-primary/10 border-2 border-primary' : 
                    isComplete ? 'bg-green-50 border-2 border-green-200' : 
                    'bg-gray-50 border-2 border-transparent'
                  }`}
                  onClick={() => setInstallStep(index)}
                >
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                    isActive ? 'bg-primary text-white' :
                    isComplete ? 'bg-green-500 text-white' :
                    'bg-gray-200 text-gray-500'
                  }`}>
                    {isComplete ? <Check className="w-5 h-5" /> : <Icon className="w-5 h-5" />}
                  </div>
                  <div className="flex-1">
                    <p className={`font-semibold ${isActive ? 'text-primary' : 'text-gray-800'}`}>
                      {index + 1}. {step.text}
                    </p>
                    <p className="text-sm text-gray-500 mt-1">{step.subtext}</p>
                  </div>
                </div>
              );
            })}
          </div>
          
          <div className="p-4 border-t bg-gray-50">
            <Button 
              onClick={handleDismiss} 
              variant="outline" 
              className="w-full"
            >
              I'll do it later
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Main Install Banner
  return (
    <div className="fixed bottom-20 left-4 right-4 sm:left-auto sm:right-4 sm:w-96 z-50 animate-slide-up">
      <div className="bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden">
        <div className="bg-gradient-to-r from-primary to-orange-500 h-2" />
        
        <div className="p-5">
          <div className="flex items-start gap-4">
            <div className="w-14 h-14 bg-gradient-to-br from-primary/10 to-orange-100 rounded-2xl flex items-center justify-center flex-shrink-0">
              <Smartphone className="w-7 h-7 text-primary" />
            </div>
            
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between mb-1">
                <h3 className="font-bold text-gray-900">Get the App</h3>
                <button 
                  onClick={handleDismiss}
                  className="p-1.5 hover:bg-gray-100 rounded-full transition-colors -mr-1"
                >
                  <X className="w-4 h-4 text-gray-400" />
                </button>
              </div>
              <p className="text-sm text-gray-500 leading-relaxed">
                Install Afriqoma for faster access, offline support, and push notifications.
              </p>
            </div>
          </div>
          
          <div className="flex gap-3 mt-4">
            <Button 
              onClick={handleInstall}
              className="flex-1 bg-primary hover:bg-primary/90"
            >
              <Download className="w-4 h-4 mr-2" />
              Install
            </Button>
            <Button 
              onClick={handleDismiss}
              variant="outline"
              className="flex-shrink-0"
            >
              Not now
            </Button>
          </div>
          
          <div className="flex items-center gap-4 mt-4 pt-4 border-t text-xs text-gray-400">
            <span className="flex items-center gap-1">
              <Check className="w-3 h-3" /> Offline access
            </span>
            <span className="flex items-center gap-1">
              <Check className="w-3 h-3" /> Push alerts
            </span>
            <span className="flex items-center gap-1">
              <Check className="w-3 h-3" /> Fast loading
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

// Push Notification Permission Component
export const PushNotificationPrompt = ({ onEnable }) => {
  const [showPrompt, setShowPrompt] = useState(false);
  const [permission, setPermission] = useState('default');

  useEffect(() => {
    if (!('Notification' in window) || !('serviceWorker' in navigator)) return;
    
    setPermission(Notification.permission);
    
    if (Notification.permission === 'default') {
      // Show after user has been on site for a while
      const timer = setTimeout(() => setShowPrompt(true), 60000);
      return () => clearTimeout(timer);
    }
  }, []);

  const requestPermission = async () => {
    try {
      const result = await Notification.requestPermission();
      setPermission(result);
      
      if (result === 'granted') {
        setShowPrompt(false);
        onEnable?.();
        
        // Register for push notifications
        const registration = await navigator.serviceWorker.ready;
        
        // Show a test notification
        new Notification('Notifications Enabled!', {
          body: 'You\'ll now receive updates about your orders and RFQs.',
          icon: '/logo.png',
          badge: '/icons/icon-72x72.png'
        });
      }
    } catch (error) {
      // Permission errors are non-critical - PWA still works
      if (process.env.NODE_ENV === 'development') {
        if (process.env.NODE_ENV === 'development') console.error('Notification permission error:', error);
      }
    }
  };

  if (!showPrompt || permission !== 'default') return null;

  return (
    <div className="fixed bottom-20 left-4 right-4 sm:left-auto sm:right-4 sm:w-96 z-50 animate-slide-up">
      <div className="bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden">
        <div className="p-5">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
              <Bell className="w-6 h-6 text-blue-600" />
            </div>
            
            <div className="flex-1">
              <div className="flex items-center justify-between mb-1">
                <h3 className="font-bold text-gray-900">Stay Updated</h3>
                <button 
                  onClick={() => setShowPrompt(false)}
                  className="p-1.5 hover:bg-gray-100 rounded-full transition-colors"
                >
                  <X className="w-4 h-4 text-gray-400" />
                </button>
              </div>
              <p className="text-sm text-gray-500">
                Get instant alerts for new RFQs, order updates, and supplier messages.
              </p>
            </div>
          </div>
          
          <div className="flex gap-3 mt-4">
            <Button onClick={requestPermission} className="flex-1">
              <Bell className="w-4 h-4 mr-2" />
              Enable Notifications
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

// App Update Available Banner
export const UpdateAvailableBanner = () => {
  const [showUpdate, setShowUpdate] = useState(false);
  const [waitingWorker, setWaitingWorker] = useState(null);

  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;

    const handleUpdate = (registration) => {
      setWaitingWorker(registration.waiting);
      setShowUpdate(true);
    };

    navigator.serviceWorker.ready.then((registration) => {
      registration.addEventListener('updatefound', () => {
        const newWorker = registration.installing;
        newWorker?.addEventListener('statechange', () => {
          if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
            handleUpdate(registration);
          }
        });
      });
    });

    // Check for updates periodically
    const interval = setInterval(() => {
      navigator.serviceWorker.ready.then((registration) => {
        registration.update();
      });
    }, 60 * 60 * 1000); // Check every hour

    return () => clearInterval(interval);
  }, []);

  const handleUpdate = () => {
    if (waitingWorker) {
      waitingWorker.postMessage({ type: 'SKIP_WAITING' });
      window.location.reload();
    }
  };

  if (!showUpdate) return null;

  return (
    <div className="fixed top-4 left-4 right-4 sm:left-auto sm:right-4 sm:w-96 z-50 animate-slide-down">
      <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl shadow-lg p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
              <Download className="w-5 h-5" />
            </div>
            <div>
              <p className="font-semibold">Update Available</p>
              <p className="text-sm text-white/80">New features and improvements</p>
            </div>
          </div>
          <Button 
            onClick={handleUpdate}
            size="sm"
            className="bg-white text-blue-600 hover:bg-white/90"
          >
            Update
            <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      </div>
    </div>
  );
};

// Offline Indicator
export const OfflineIndicator = () => {
  const [isOffline, setIsOffline] = useState(!navigator.onLine);

  useEffect(() => {
    const handleOnline = () => setIsOffline(false);
    const handleOffline = () => setIsOffline(true);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  if (!isOffline) return null;

  return (
    <div 
      data-testid="offline-indicator"
      className="fixed top-0 left-0 right-0 z-[100] bg-amber-500 text-white py-2 px-4 text-center text-sm font-medium animate-slide-down"
    >
      <span className="inline-flex items-center gap-2">
        <span className="w-2 h-2 bg-white rounded-full animate-pulse" />
        You're offline. Some features may be limited.
      </span>
    </div>
  );
};

export default PWAInstallPrompt;
